import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { SearchOutlined } from '@ant-design/icons';
import { Spin, Table, Row, Col, Input, Button, Drawer, Space } from 'antd';
import { CSVLink } from 'react-csv';
import { delay } from '../../helpers/helpers';
import cookies from 'js-cookie';
import axiosConfig from '../../utils/axios-config';
import { useSelector } from 'react-redux';
import './logging-details-table.scss';
import { useHistory, useParams } from 'react-router-dom';

const LoggingDetailsTable = () => {
    const params = useParams();
    let history = useHistory();
    const intervalRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchCriteria, setSearchCriteria] = useState({});
    const [currentEntry, setCurrentEntry] = useState('');
    const currentUser = useSelector((state) => state.auth.currentUser);
    const searchInput = useRef(null);

    const [sortInfo, setSortInfo] = useState({ columnKey: null, order: null });

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
    };

    const handleReset = (clearFilters) => {
        clearFilters();
    };

    const compareByColumn = (a, b, dataIndex) => {
        const valA = a[dataIndex];
        const valB = b[dataIndex];
        if (valA == null && valB == null) return 0;
        if (valA == null) return -1;
        if (valB == null) return 1;

        const numA = parseFloat(valA);
        const numB = parseFloat(valB);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;

        const mA = moment(valA);
        const mB = moment(valB);
        if (mA.isValid() && mB.isValid()) return mA.valueOf() - mB.valueOf();

        return valA.toString().localeCompare(valB.toString());
    };

    useEffect(() => {
        if (currentUser?.id) {
            fetchTable();
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [currentUser]);

    const handleStart = () => {
        if (!intervalRef.current && currentUser?.id) {
            const desiredSort = { columnKey: "no", order: "descend" };
            setSortInfo(desiredSort);
            fetchTable(desiredSort);
            intervalRef.current = setInterval(() => {
                fetchTable(desiredSort);
            }, 5000);
            setIsRunning(true);
        }
    };

    const handleStop = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            const desiredSort = { columnKey: "no", order: "ascend" };
            setSortInfo(desiredSort);
            intervalRef.current = null;
            setIsRunning(false);
        }
    };

    const onClose = () => setOpen(false);

    useEffect(() => {
        applySearchCriteria();
    }, [searchCriteria]);

    const applySearchCriteria = () => {
        let filterData = tableData;
        if (searchCriteria.keyword && searchCriteria.keyword.length >= 3) {
            filterData = filterData.filter(
                (row) =>
                    (row.fullEntry && row.fullEntry.toString().toLowerCase().includes(searchCriteria.keyword.toLowerCase())) ||
                    (row.sowName && row.sowName.toString().toLowerCase().includes(searchCriteria.keyword.toLowerCase())) ||
                    (row.sowStepName && row.sowStepName.toString().toLowerCase().includes(searchCriteria.keyword.toLowerCase()))
            );
        }
        if (sortInfo?.columnKey) {
            const sorted = [...filterData].sort((a, b) => compareByColumn(a, b, sortInfo.columnKey));
            if (sortInfo.order === 'descend') sorted.reverse();
            setFilteredData(sorted);
        } else {
            setFilteredData(filterData);
        }
    };

    const filterByKeyword = (e) => {
        const val = e.target.value;
        delay(function () {
            setSearchCriteria((prev) => ({ ...prev, keyword: val }));
        }, 500);
    };

    const showFullEntry = (entryText) => {
        setCurrentEntry(entryText);
        setOpen(true);
    };

    const changePage = (page) => {
        if (page === 'main-logging') history.push(`/rpa/main-logging`);
    };

    const getColumnProps = (dataIndex, { search = false, sort = false } = {}) => {
        const props = {};
        if (search) {
            Object.assign(props, {
                filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                    <div style={{ padding: 8 }}>
                        <Input
                            ref={searchInput}
                            placeholder={`Search ${dataIndex}`}
                            value={selectedKeys[0]}
                            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                            style={{ marginBottom: 8, display: "block" }}
                        />
                        <Space>
                            <Button type="primary" onClick={() => handleSearch(selectedKeys, confirm, dataIndex)} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
                                Search
                            </Button>
                            <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                                Reset
                            </Button>
                        </Space>
                    </div>
                ),
                filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
                onFilter: (value, record) => record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
            });
        }
        if (sort) {
            Object.assign(props, {
                sorter: (a, b) => compareByColumn(a, b, dataIndex),
                sortDirections: ["ascend", "descend"],
                sortOrder: sortInfo.columnKey === dataIndex ? sortInfo.order : null,
            });
        }
        return props;
    };

    const fetchTable = async (overrideSort) => {
        const activeSort = overrideSort || sortInfo;
        setLoading(true);
        try {
            const response = await axiosConfig({
                method: 'get',
                url: `/api/rpa/main-loggings/${params.loggingId}/log-entries`,
                headers: { Authorization: `Bearer ${cookies.get('access_token')}` },
            });

            const data = response.data.data.map((res, idx) => {
                const index = idx + 1;
                let entryData = res.entryData || '';
                let displayEntry = entryData;

                if (entryData && entryData.length > 100) {
                    displayEntry = (
                        <>
                            <div style={{ wordBreak: 'break-word' }}>{entryData.substring(0, 100)}...</div>
                            <Button type='link' onClick={() => showFullEntry(entryData)}>Show more</Button>
                        </>
                    );
                }

                return {
                    key: res.id,
                    no: index,
                    entryData: displayEntry,
                    fullEntry: entryData,
                    sowName: res.sowName,
                    sowStepNumber: res.sowStepNumber,
                    sowStepName: res.sowStepName,
                    time: res.time,
                };
            });

            setTableData(data);
            setFilteredData(data);
            setLoading(false);
        } catch (error) {
            console.error(error);
            setLoading(false);
        }
    };

    const handleTableChange = (pagination, filters, sorter) => {
        setSortInfo({ columnKey: sorter.columnKey, order: sorter.order });
    };

    const columns = [
        { title: 'Id', dataIndex: 'no', key: 'no', width: 80, ...getColumnProps("no", { sort: true }) },
        { title: 'Entry Data', dataIndex: 'entryData', key: 'entryData', width: 300, ...getColumnProps("entryData", { search: true }) },
        { title: 'Sow Name', dataIndex: 'sowName', key: 'sowName', width: 200, ...getColumnProps("sowName", { search: true }) },
        { title: 'Step No', dataIndex: 'sowStepNumber', key: 'sowStepNumber', width: 100 },
        { title: 'Sow Step Name', dataIndex: 'sowStepName', key: 'sowStepName', width: 200, ...getColumnProps("sowStepName", { search: true }) },
        { 
            title: 'Time', 
            dataIndex: 'time', 
            key: 'time', 
            width: 200,
            render: (t) => t ? moment(t).format("DD MMM, YYYY hh:mm:ss A") : '',
            ...getColumnProps("time", { sort: true }) 
        },
    ];

    return (
        <div className="logging-details-container" style={{ width: '100%', padding: '20px' }}>
            <Spin spinning={loading}>
                <Row className='search-area' gutter={[16, 16]} align="middle" style={{ marginBottom: 20 }}>
                    <Col xs={24} md={12}>
                        <Space wrap>
                            <Button type="primary" onClick={handleStart} disabled={isRunning}>Start Auto-Refresh</Button>
                            <Button danger onClick={handleStop} disabled={!isRunning}>Stop Auto-Refresh</Button>
                            <Button onClick={() => changePage('main-logging')}>Back to List</Button>
                        </Space>
                    </Col>
                    <Col xs={24} md={12}>
                        <Input
                            onChange={filterByKeyword}
                            addonBefore={<SearchOutlined />}
                            placeholder='Global Search (Min 3 chars)'
                        />
                    </Col>
                </Row>

                <div style={{ marginBottom: 16 }}>
                    <CSVLink filename={'LoggingContent.csv'} data={tableData} className='ant-btn ant-btn-default'>
                        Download CSV
                    </CSVLink>
                </div>

                <Table
                    columns={columns}
                    dataSource={filteredData}
                    pagination={{ defaultPageSize: 100, showSizeChanger: true }}
                    onChange={handleTableChange}
                    scroll={{ x: 'max-content' }} // Makes table horizontal scrollable on small screens
                    bordered
                    size="middle"
                />
            </Spin>

            {/* Removing getContainer={false} makes it relative to the browser window */}
            <Drawer
                title='Entry Data Details'
                placement='bottom'
                closable={true}
                onClose={onClose}
                height="50vh" // Takes up half the screen height
                visible={open}
                destroyOnClose={true}
            >
                <div style={{ 
                    background: '#f5f5f5', 
                    padding: '15px', 
                    borderRadius: '4px',
                    border: '1px solid #d9d9d9'
                }}>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                        {currentEntry}
                    </pre>
                </div>
            </Drawer>
        </div>
    );
};

export default LoggingDetailsTable;
